import type {
  Author,
  AuthorRelationResponseCollection,
  ComponentCommonAuthorDetailed,
} from '@edwin-group/people-graphql';
import { useResponsiveImage } from '@/utils/useResponsiveImage';
import classNames from 'classnames';
import { CustomImage } from '@/components/CustomImage';

function AuthorImage({ author }: { author: Author }) {
  const { src, alt } = useResponsiveImage(author?.attributes?.avatar);
  return <CustomImage src={src} alt={alt} className="h-20 w-20 shrink-0 rounded-full object-cover object-center" />;
}

const authorRowClassName = 'grid w-full max-w-full grid-cols-[80px_1fr] gap-sh-16 md:grid-cols-[80px_1fr]';

type DefaultContentTemplateAuthorDetailedVariant = 'default' | 'learn';

export default function DefaultContentTemplateAuthorDetailed({
  component,
  className,
  variant = 'default',
}: {
  component: ComponentCommonAuthorDetailed;
  className?: string;
  variant?: DefaultContentTemplateAuthorDetailedVariant;
}) {
  const authors = (component?.authors as unknown as AuthorRelationResponseCollection)?.data ?? [];

  const containerClassName = classNames(
    'my-sh-30 rounded-sh-16 bg-light-green p-sh-16',
    {
      'max-w-[360px]': variant === 'default',
    },
    className,
  );

  const shouldShowAuthorsTitle =
    (variant === 'default' && authors.length > 1) || (variant === 'learn' && authors.length >= 1);

  if (!authors.length) {
    return null;
  }

  return (
    <div className={containerClassName}>
      {shouldShowAuthorsTitle && (
        <h2 className="mb-sh-16 font-haffer text-sh-12-18 font-bold text-dark-grey">Authors</h2>
      )}
      <div className="flex flex-col gap-sh-30">
        {authors.map((author) => (
          <div key={author?.id} className="flex flex-col gap-sh-30">
            <div className={authorRowClassName}>
              <div className="flex shrink-0 justify-start">
                <AuthorImage author={author} />
              </div>
              <div className="flex min-w-0 flex-col justify-center gap-0">
                {author?.attributes?.name ? (
                  <p className="font-haffer text-sh-12-18 font-bold text-dark-grey">{author.attributes.name}</p>
                ) : null}
                {author?.attributes?.bio ? (
                  <p className="mt-1 font-haffersq text-sh-12-18 font-normal text-dark-grey">{author.attributes.bio}</p>
                ) : null}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
